/**
 * Staff tenant branding tab - the Theme editor shown on the staff tenant detail
 * page (`/staff/tenants/<uuid>`). Reuses the shared `ThemeEditor`, wired to the
 * staff endpoint (keyed by tenant uuid) since staff manage tenants they don't
 * belong to.
 *
 * The Welcome-page editor moved to the org-admin Settings area (Settings >
 * Welcome page); it is no longer authored from the staff portal.
 */

import { useMutation, useQuery } from '@tanstack/react-query'
import { Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import api from '@/admin/lib/api'
import { toastApiError } from '@/shared/lib/api-error'
import { ThemeEditor } from '@/admin/components/theme-editor'
import type { ThemeOverrides } from '@/shared/lib/theme'

export function StaffThemeTab({ uuid }: { uuid: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ['staff-tenant-theme', uuid],
    queryFn: () =>
      api.get(`/staff/tenants/${uuid}/theme/`).then((res) => res.data),
    enabled: Boolean(uuid),
  })

  const save = useMutation({
    mutationFn: (theme: ThemeOverrides) =>
      api.put(`/staff/tenants/${uuid}/theme/`, { theme }).then((res) => res.data),
    onSuccess: () => toast.success('Theme saved'),
    onError: (err) => toastApiError(err, 'Failed to save theme'),
  })

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-5 w-5 animate-spin text-[var(--ink-3)]" />
      </div>
    )
  }

  return (
    <ThemeEditor
      theme={(data?.theme as ThemeOverrides) ?? {}}
      seedKey={uuid}
      saving={save.isPending}
      onSave={(theme) => save.mutate(theme)}
    />
  )
}
